import PlaidLogo from 'plaid-threads/Icons/PlaidLogo';
import cx from 'classnames';
import styled from 'threads5/themes/styled';
import SxProps from 'threads5/themes/sx-props';
import { ITheme as Theme } from 'threads5/themes/theme';

import { createElement, useState, type FC } from 'react';
import FoundationIcon, {
  useUniqueSVGClipPathId,
  type FoundationIconName,
} from './FoundationIcon';
import type { Icons } from './Icon.types';

const StyledIconWrapper = styled('span')({
  display: 'contents',
});

/** Wrapper helper for legacy Plaid Threads icons to force unique clip path IDs. */
const wrap = <T,>(component: FC<T>) => {
  return (props: T) => {
    const [spanElement, setSpanElement] = useState<HTMLSpanElement | null>(
      null,
    );
    useUniqueSVGClipPathId(spanElement);

    return (
      <StyledIconWrapper ref={setSpanElement}>
        {createElement(component, props)}
      </StyledIconWrapper>
    );
  };
};

const StyledPlaidLogo = wrap(styled(PlaidLogo)({}));

export interface IIconProps {
  icon: Icons | FoundationIconName;
  sx?: SxProps<Theme>;
  size?: 14 | 16 | 18 | 20;
  className?: string;
}

const renderIcon = (
  icon: IIconProps['icon'],
  rest: Omit<IIconProps, 'icon' | 'className'>,
) => {
  switch (icon) {
    case 'Beacon':
      return (
        <FoundationIcon icon='ProductBeacon' omitWrapper={true} {...rest} />
      );
    case 'ChevronLeftDouble':
      return (
        <FoundationIcon icon='ChevronDoubleLeft' omitWrapper={true} {...rest} />
      );
    case 'PlaidLogo':
      return <StyledPlaidLogo {...rest} />;
    default:
      return <FoundationIcon icon={icon} omitWrapper={true} {...rest} />;
  }
};

const Icon = ({ icon, className, ...rest }: IIconProps) => {
  return (
    <span className={cx('icon-container', className)}>
      {renderIcon(icon, rest)}
    </span>
  );
};

export default Icon;
